import { useEffect, useRef } from 'react';
import { getChannelInfo, useAppOnlineStatus, useLastMessageObject } from '../../../Slice';
import Store from '../../../Store';
import { handleMarkAsRead } from '../channelActions';

/**
 * Custom hook to mark the first message in a channel as read when conditions are met
 * This hook should be called from message components to reactively handle mark as read
 *
 * @param {string} channelId - The channel ID
 * @param {object} messageObject - The message object being rendered
 * @param {number} index - The index of the message in the list (0 is the newest)
 */
export const useMarkMessageAsRead = (channelId, messageObject, index) => {
   const isAppOnline = useAppOnlineStatus();
   const lastMessageObject = useLastMessageObject(channelId);
   const channelInfo = getChannelInfo(channelId);
   const hasMarkedRef = useRef(false);

   useEffect(() => {
      // Only process for the first actual message (index 0)
      if (index !== 0) return;
      if (!isAppOnline) return;
      if (!channelId || !messageObject) return;

      const lastReadMessageId = channelInfo?.lastReadMessage?.messageId;
      const unreadCount = channelInfo?.unreadCount || 0;

      // Check if the message has sequenceId - if not, it's not yet published to server
      // Skip marking as read for unpublished messages
      // This check must come first to ensure we never mark as read without sequenceId
      if (!messageObject.sequenceId) {
         return;
      }

      // Handle unreadCount -1 case (only if message has sequenceId)
      if (unreadCount === -1 && !hasMarkedRef.current) {
         handleMarkAsRead({
            channelId,
            messageId: '',
         });
         hasMarkedRef.current = true;
         return;
      }

      // Check if user is viewing the channel
      const isViewing = isUserViewingChannel(channelId);
      if (!isViewing) return;
      // Check if we should mark as read
      const shouldMark = shouldMarkAsRead(lastMessageObject, lastReadMessageId);
      const seenAt = lastMessageObject?.status?.seenAt;

      // Only mark as read if seenAt is undefined, not available, or empty string
      if (shouldMark && (!seenAt || seenAt === '')) {

         handleMarkAsRead({
            channelId,
            messageId: unreadCount === -1 ? '' : messageObject.messageId,
         });
         hasMarkedRef.current = true;
      }
   }, [channelId, messageObject, messageObject?.sequenceId, index, isAppOnline, channelInfo, lastMessageObject]);

   // Reset the ref when channelId changes
   useEffect(() => {
      hasMarkedRef.current = false;
   }, [channelId]);
};

/**
 * Check if user is currently viewing the channel
 */
const isUserViewingChannel = channelId => {
   try {
      const activeChannel = Store.getState().channelNavData?.activeConversationChannel;
      return activeChannel?.channelId === channelId;
   } catch (error) {
      return false;
   }
};

/**
 * Common utility to check if should mark as read
 */
const shouldMarkAsRead = (firstActualMessage, lastReadMessageId, lastReadMessageData) => {
   return (
      !lastReadMessageId ||
      firstActualMessage.messageId !== lastReadMessageId ||
      (lastReadMessageData && firstActualMessage.messageTime > lastReadMessageData.messageTime)
   );
};
